/* eslint-disable react-hooks/exhaustive-deps */
import { cloneDeep, isEmpty, isArray, set, isEqual, pick, uniqBy } from 'lodash';
import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import { createOptions, getYears, monthsPlaceholder, checkArray, Page_Size } from '../../../Components/helper';
import { fetchAllSeasonsAction, fetchAllBrandAction, fetchAllSupplierTypeAction, filterSortData } from '../store/placeholder.actions';
import { fetchCategoryAction } from '../../Dashboard/TodoList/Samples/store/TodoList.action';
import { localStorageService } from '../../../services/localStorage.service';
import { getPlacementSubBrandDetails } from '../../SystemMasters/Store/SystemMasters.action';
import { IdNameOption, DynamicColumn, PlaceholderIndividualDetail } from '../types';
import { PlaceholderDetailsState } from '../store/placeholder.reducer';

type PlaceholderThunkDispatch = ThunkDispatch<unknown, unknown, AnyAction>;

interface DynamicColumnColId {
  isLable?: boolean | undefined;
  keyName?: string | undefined;
  refName?: string | undefined;
  seqNumber?: number | undefined;
  type?: string | undefined;
  _id?: string | undefined;
}

export interface PlaceholderDynamicColumn extends DynamicColumn {
  isLable?: boolean;
  keyName?: string;
  refName?: string;
  seqNumber?: number;
  lableId?: string;
  options?: PlaceholderDynamicColumn[];
  selectedOpt?: unknown;
  valueId?: string | null;
  colId?: DynamicColumnColId;
  isDeleted?: boolean;
}

/**
 * A row's selected values for one Multi Custom Attribute label — `valId` holds
 * every selected option id. `colId`/each `valId` entry may arrive as a raw id
 * (freshly set client-side) or a populated `{ _id, keyName }` object (fetched
 * from the backend); consumers normalise via `idOf()`.
 */
export interface PlaceholderMultiDynamicColumn {
  colId: string | { _id?: string };
  valId: Array<string | { _id?: string }>;
}

export interface LabelOption {
  _id: string | number;
  name?: string | number;
  label?: string | number;
  value?: string | number;
}

export interface PlaceholderRow {
  _id?: string;
  month?: string | null;
  year?: string | number | { _id?: string | number; name?: string | number } | null;
  catOneId?: IdNameOption | null;
  catTwoId?: IdNameOption | null;
  catThreeId?: IdNameOption | null;
  catFourId?: IdNameOption | null;
  productTypeId?: IdNameOption | null;
  productDesc?: string;
  description?: string;
  brandId?: IdNameOption | null;
  subBrandId?: IdNameOption | null;
  seasonId?: IdNameOption | null;
  placeholderQuantity?: string | number;
  actualQuantity?: string | number | null;
  isAddRow?: boolean;
  isExpand?: boolean;
  isDefault?: boolean;
  category3Options?: LabelOption[];
  category4Options?: LabelOption[];
  productList?: unknown[];
  placeholderStatus?: string;
  dynamicColumn?: PlaceholderDynamicColumn[];
  deletedColumn?: PlaceholderDynamicColumn[];
  multiCustomAttribute?: PlaceholderMultiDynamicColumn[];
}

export interface PlaceholderUiState {
  placeholderManagement: PlaceholderRow[];
  loader: boolean;
  yearOptions: LabelOption[];
  monthOptions: LabelOption[];
  category1Options: LabelOption[];
  category2Options: LabelOption[];
  category3Options: LabelOption[];
  category4Options: LabelOption[];
  seasonOptions: LabelOption[];
  productTypeOptions: LabelOption[];
  brandOptions: LabelOption[];
  subBrandOptions: LabelOption[];
  duplicateExist: number[];
  pageIndex: number;
  pageSize: number;
  isPageLoader: boolean;
  hasMore: boolean;
}

export interface PlaceholderCardDetails {
  seasonsId: string[] | undefined;
  month: string[] | undefined;
  year: Array<string | number> | undefined;
  category1: string[] | undefined;
  category2: string[] | undefined;
  productTypeId: string[] | undefined;
  brandId: string[] | undefined;
  subBrandId: string[] | undefined;
}

export type PlaceholderGlobalError = Record<string, Record<string, string>>;

type YearLike = { _id?: string | number; name?: string | number };

interface CreateObjInput {
  brandId?: IdNameOption | null;
  subBrandId?: IdNameOption | null;
  seasonId?: IdNameOption | null;
  productTypeId?: IdNameOption | null;
  catOneId?: IdNameOption | null;
  catTwoId?: IdNameOption | null;
  catThreeId?: IdNameOption | null;
  productDesc?: string;
  year?: unknown;
}

type NamePair = { _id?: string | undefined; name?: string | undefined };
type YearPair = { _id?: string | number | undefined; name?: string | number | undefined };

interface CreatedObj {
  brandId: NamePair;
  subBrandId: NamePair;
  seasonId: NamePair;
  productTypeId: NamePair;
  catOneId: NamePair;
  catTwoId: NamePair;
  catThreeId: NamePair;
  productDesc?: string | undefined;
  year: YearPair;
}

interface UpdatedProduct {
  _id?: string | undefined;
  description?: string | undefined;
  catThreeId?: IdNameOption | null | undefined;
  catFourId?: IdNameOption | null | undefined;
  dynamicColumn?: PlaceholderDynamicColumn[] | undefined;
  multiCustomAttribute?: PlaceholderMultiDynamicColumn[] | undefined;
  brandId?: IdNameOption | null | undefined;
  month?: string | null | undefined;
}

interface PlaceholderReduxState {
  PlaceholderDetails: PlaceholderDetailsState;
  UserDetails: { userInfo: { userId: string } };
}

interface PlaceholderStateContextValue {
  placeholderState: PlaceholderUiState;
  globalError: PlaceholderGlobalError;
  totalCount: number;
  cardDetails: PlaceholderCardDetails;
  individualDetail: PlaceholderIndividualDetail | null;
  placeholderId: string;
}

interface PlaceholderActionsContextValue {
  setPlaceholderState: React.Dispatch<React.SetStateAction<PlaceholderUiState>>;
  objKeys: string[];
  converDropdownData: (allTypeData: PlaceholderDynamicColumn[]) => Promise<PlaceholderDynamicColumn[]>;
  getUpdatedProducts: (mainData: PlaceholderRow[], modifiedData: PlaceholderRow[]) => Promise<UpdatedProduct[]>;
  checkValidation: (selectedIdsArray?: string[]) => boolean;
  createObj: (obj: CreateObjInput) => CreatedObj;
  checkDuplicate: (array: PlaceholderRow[]) => Promise<{ is_Valid: boolean; unique: number[] }>;
  addNewRow: () => Promise<void>;
}

export type PlaceholderContextValue = PlaceholderStateContextValue & PlaceholderActionsContextValue;

export const StateContext = createContext<PlaceholderStateContextValue | null>(null);
const ActionsContext = createContext<PlaceholderActionsContextValue | null>(null);

interface PlaceholderProviderProps {
  children: React.ReactNode;
}

/**
 * Provider for the Placeholder Management detail page.
 * State lives in StateContext and stable callbacks in ActionsContext so
 * dispatch-only consumers do not re-render when state changes, while
 * `usePlaceholderState()` merges both to keep the consumer API 1:1 with JS.
 */
export default function PlaceholderManagementStateProvider({ children }: PlaceholderProviderProps): React.ReactElement {
  const {
    PlaceholderDetails: { placeholderList, dynamicColumn, totalCount, individualDetail },
    UserDetails: {
      userInfo: { userId },
    },
  } = useSelector((state: PlaceholderReduxState) => state);
  const dispatch = useDispatch<PlaceholderThunkDispatch>();
  const params = useParams<{ placeholderId?: string; id?: string }>();
  const placeholderId = params.placeholderId ?? params.id ?? '';
  const localStorageFilter = localStorageService.get(`placeholderFilter_${placeholderId}`);
  const [globalError, setGlobalErrors] = useState<PlaceholderGlobalError>({});

  const tempObj: PlaceholderRow = {
    month: null,
    year: null,
    catOneId: null,
    catTwoId: null,
    catThreeId: null,
    catFourId: null,
    productTypeId: null,
    productDesc: '',
    brandId: null,
    subBrandId: null,
    seasonId: null,
    placeholderQuantity: '',
    actualQuantity: null,
    isAddRow: true,
    isExpand: false,
    category3Options: [],
    category4Options: [],
    isDefault: true,
    placeholderStatus: 'draft',
    dynamicColumn,
    multiCustomAttribute: [],
  };

  const initialSetting: PlaceholderUiState = {
    placeholderManagement: [],
    loader: false,
    yearOptions: createOptions(getYears(), 'name', '_id'),
    monthOptions: createOptions(monthsPlaceholder, 'name', '_id'),
    category1Options: [],
    category2Options: [],
    category3Options: [],
    category4Options: [],
    seasonOptions: [],
    productTypeOptions: [],
    brandOptions: [],
    subBrandOptions: [],
    duplicateExist: [],
    pageIndex: 1,
    pageSize: Page_Size,
    isPageLoader: false,
    hasMore: false,
  };
  const [placeholderState, setPlaceholderState] = useState<PlaceholderUiState>(initialSetting);
  const [cardDetails, setCardDetails] = useState<PlaceholderCardDetails>({
    seasonsId: [],
    month: [],
    year: [],
    category1: [],
    category2: [],
    productTypeId: [],
    brandId: [],
    subBrandId: [],
  });

  const placeholderStateRef = useRef<PlaceholderUiState>(placeholderState);
  useEffect(() => {
    placeholderStateRef.current = placeholderState;
  }, [placeholderState]);

  const getInitialObj = (baseObj: PlaceholderRow, manipuLatedDynamicColumn?: PlaceholderDynamicColumn[]): PlaceholderRow[] => {
    const objdata = cloneDeep([{ ...baseObj }]);
    const mappedColumn = manipuLatedDynamicColumn?.map((dyn) => {
      return {
        ...dyn,
        valueId: null,
        selectedOpt: null,
        colId: {
          isLable: dyn?.isLable,
          keyName: dyn?.keyName,
          refName: dyn?.refName,
          seqNumber: dyn?.seqNumber,
          type: dyn?.type,
          _id: dyn?._id,
        },
      };
    });
    if (mappedColumn) {
      objdata[0].dynamicColumn = [...mappedColumn];
    }
    return objdata;
  };

  useEffect(() => {
    if (individualDetail) {
      setCardDetails((prev) => ({
        ...prev,
        seasonsId: individualDetail?.season?.map((item) => item._id),
        month: individualDetail.month,
        year: individualDetail.year,
        category1: individualDetail?.catOneId?.map((item) => item._id),
        category2: individualDetail?.catTwoId?.map((item) => item._id),
        productTypeId: individualDetail?.productType?.map((item) => item._id),
        brandId: individualDetail?.brand?.map((item) => item._id),
        subBrandId: individualDetail?.subBrand?.map((item) => item._id),
      }));
      setPlaceholderState((prevState) => ({
        ...prevState,
        yearOptions:
          (individualDetail?.year?.length ?? 0) > 0
            ? (individualDetail?.year?.map((item) => ({ _id: item, name: item })) ?? initialSetting.yearOptions)
            : initialSetting?.yearOptions,
        monthOptions:
          (individualDetail?.month?.length ?? 0) > 0
            ? (individualDetail?.month?.map((item) => ({ _id: item, name: item })) ?? initialSetting.monthOptions)
            : initialSetting?.monthOptions,
      }));
    }
  }, [individualDetail]);

  const converDropdownData = useCallback(async (allTypeData: PlaceholderDynamicColumn[]): Promise<PlaceholderDynamicColumn[]> => {
    const allData: PlaceholderDynamicColumn[] = cloneDeep(allTypeData);
    let filteredData: PlaceholderDynamicColumn[] = [];
    let filteredDropdownData = allData?.filter((ele) => ele.type === 'dropdown' && ele.isLable === true);
    const filteredTextData = allData?.filter((ele) => ele.type === 'text' && ele.isLable === false);
    let filteredRadioData = allData?.filter((ele) => ele.type === 'radio-button' && ele.isLable === true);
    const filteredDateData = allData?.filter((ele) => ele.type === 'date-picker');
    if (!isEmpty(filteredDropdownData)) {
      filteredDropdownData = filteredDropdownData.map((item) => {
        const valuesForLabel = allData.filter((ele) => item._id === ele.lableId);
        item.options = valuesForLabel;
        if ('selectedOpt' in item || 'valueId' in item) {
          delete item.selectedOpt;
          delete item.valueId;
        }
        return item;
      });
    }
    if (!isEmpty(filteredRadioData)) {
      filteredRadioData = filteredRadioData.map((item) => {
        const valuesForLabel = allData.filter((ele) => item._id === ele.lableId);
        item.options = valuesForLabel;
        if ('selectedOpt' in item || 'valueId' in item) {
          delete item.selectedOpt;
          delete item.valueId;
        }
        return item;
      });
    }
    if (isArray(filteredDropdownData) && isArray(filteredTextData)) {
      filteredData = filteredDropdownData.concat(filteredTextData);
    }
    if (isArray(filteredRadioData)) filteredData = filteredData.concat(filteredRadioData);
    if (isArray(filteredDateData)) filteredData = filteredData.concat(filteredDateData);
    return cloneDeep(filteredData);
  }, []);

  /**
   * Appends a fresh blank default placeholder row (the template row) to the end
   * of the grid. Backs the floating "+" action that creates a new draft row.
   */
  const addNewRow = useCallback(async () => {
    const manipuLatedDynamicColumn = await converDropdownData(dynamicColumn);
    const newRow = cloneDeep(getInitialObj(tempObj, manipuLatedDynamicColumn)?.[0]);

    if (individualDetail?.brand?.length === 1) newRow.brandId = individualDetail.brand[0];
    if (individualDetail?.subBrand?.length === 1) newRow.subBrandId = individualDetail.subBrand[0];
    if (individualDetail?.season?.length === 1) newRow.seasonId = individualDetail.season[0];
    if (individualDetail?.productType?.length === 1) newRow.productTypeId = individualDetail.productType[0];
    if (individualDetail?.catOneId?.length === 1) newRow.catOneId = individualDetail.catOneId[0];
    if (individualDetail?.catTwoId?.length === 1) newRow.catTwoId = individualDetail.catTwoId[0];
    if (individualDetail?.month?.length === 1) newRow.month = individualDetail.month[0];
    if (individualDetail?.year?.length === 1) newRow.year = individualDetail.year[0];

    setPlaceholderState((prevState) => ({
      ...prevState,
      placeholderManagement: [...prevState.placeholderManagement, newRow],
    }));
  }, [dynamicColumn, converDropdownData, individualDetail]);

  useEffect(() => {
    const init = async () => {
      const copyPlaceholderList = cloneDeep(placeholderList) as unknown as PlaceholderRow[];
      const manipuLatedDynamicColumn = await converDropdownData(dynamicColumn);
      if (checkArray(copyPlaceholderList)) {
        for (const placehold of copyPlaceholderList) {
          const newDynamicColumn: PlaceholderDynamicColumn[] = [];
          const deletedDyna: PlaceholderDynamicColumn[] = [];
          placehold?.dynamicColumn?.forEach((placeDy) => {
            const updatedData = placeDy;
            const foundDAta = manipuLatedDynamicColumn?.find((val) => val?._id === updatedData?._id);
            if (!foundDAta) {
              updatedData.isDeleted = true;
              deletedDyna.push(updatedData);
            }
          });
          for (const dy of manipuLatedDynamicColumn) {
            const foundDAta = placehold?.dynamicColumn?.find((val) => val?._id === dy?._id);
            if (foundDAta) {
              const foundNewObj = { ...dy, ...foundDAta };
              if (dy?.options) foundNewObj.options = dy?.options;
              newDynamicColumn.push(foundNewObj);
            } else {
              const objss = {
                ...dy,
                colId: {
                  isLable: dy?.isLable,
                  keyName: dy?.keyName,
                  refName: dy?.refName,
                  seqNumber: dy?.seqNumber,
                  type: dy?.type,
                  _id: dy?._id,
                },
              };
              newDynamicColumn.push(objss);
            }
          }
          placehold.dynamicColumn = newDynamicColumn;
          placehold.deletedColumn = deletedDyna;
        }
        const mergedList = copyPlaceholderList.map((copyItem) => {
          const matchingItem = placeholderState?.placeholderManagement.find((item) => item._id === copyItem._id);
          return matchingItem ? { ...matchingItem, ...copyItem } : copyItem;
        });

        const newItems = copyPlaceholderList.filter((item) => !placeholderState?.placeholderManagement?.some((copyItem) => copyItem._id === item._id));

        const uniqueByPlaceholder = uniqBy([...mergedList, ...newItems], '_id');

        setPlaceholderState((prevState) => ({
          ...prevState,
          placeholderManagement: uniqueByPlaceholder,
          hasMore: totalCount > uniqueByPlaceholder?.length,
        }));
      } else {
        setPlaceholderState((prevState) => ({
          ...prevState,
          placeholderManagement: [],
          hasMore: false,
        }));
      }
    };
    if (isArray(dynamicColumn) || isArray(placeholderList)) init();
  }, [dynamicColumn, placeholderList, totalCount]);

  useEffect(() => {
    const callFn = async () => {
      const sesonres = await dispatch(fetchAllSeasonsAction({ loggedInUserId: userId, isActive: true } as unknown as string));
      const BrandRes = await dispatch(
        fetchAllBrandAction({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const placementSubBrandRes = await dispatch(
        getPlacementSubBrandDetails({
          loggedInUserId: userId,
          isActive: true,
        }),
      );
      const SupplierTypeRes = await dispatch(
        fetchAllSupplierTypeAction({
          loggedInUserId: userId,
          isActive: true,
          tier: 'Tier1',
        }),
      );
      const category = [1, 2];
      for (const cat of category) {
        const categoryRes = await dispatch(
          fetchCategoryAction({
            categoryType: cat,
            loggedInUserId: userId,
          }),
        );

        const individualCatKey = cat === 1 ? 'catOneId' : 'catTwoId';
        const hasIndividualData = individualDetail && checkArray(individualDetail[individualCatKey]);
        setPlaceholderState((prevState) => ({
          ...prevState,
          [`category${cat}Options` as 'category1Options' | 'category2Options']: hasIndividualData
            ? cloneDeep(createOptions(individualDetail?.[individualCatKey], 'name', '_id'))
            : cloneDeep(createOptions(categoryRes, 'name', '_id')),
        }));
      }

      const hasSeasonData = individualDetail && checkArray(individualDetail.season);
      if (checkArray(sesonres) || hasSeasonData) {
        const seasonOptions = hasSeasonData ? createOptions(individualDetail?.season, 'name', '_id') : createOptions<IdNameOption>(sesonres as unknown as IdNameOption[], 'name', '_id');
        setPlaceholderState((prevState) => ({
          ...prevState,
          seasonOptions,
        }));
      }

      const hasBrandData = individualDetail && checkArray(individualDetail.brand);
      if (checkArray(BrandRes) || hasBrandData) {
        const brandOptions = hasBrandData ? createOptions(individualDetail?.brand, 'name', '_id') : createOptions<IdNameOption>(BrandRes as unknown as IdNameOption[], 'name', '_id');
        setPlaceholderState((prevState) => ({
          ...prevState,
          brandOptions,
        }));
      }

      const hasSubBrandData = individualDetail && checkArray(individualDetail.subBrand);
      if (checkArray(placementSubBrandRes) || hasSubBrandData) {
        const subBrandOptions = hasSubBrandData ? createOptions(individualDetail?.subBrand, 'name', '_id') : createOptions<IdNameOption>(placementSubBrandRes as unknown as IdNameOption[], 'name', '_id');
        setPlaceholderState((prevState) => ({
          ...prevState,
          subBrandOptions,
        }));
      }

      const hasProductTypeData = individualDetail && checkArray(individualDetail.productType);
      if (checkArray(SupplierTypeRes) || hasProductTypeData) {
        const productTypeOptions = hasProductTypeData
          ? createOptions(individualDetail?.productType, 'name', '_id')
          : createOptions<IdNameOption>(SupplierTypeRes as unknown as IdNameOption[], 'name', '_id');
        setPlaceholderState((prevState) => ({
          ...prevState,
          productTypeOptions,
        }));
      }

      if (localStorageFilter) {
        const objFilter = {
          selectedSeason: localStorageFilter?.seasonsId,
          selectedYear: localStorageFilter?.year,
          selectedBrand: localStorageFilter?.brandId,
          selectedSubBrand: localStorageFilter?.subBrandId,
          selectedCategory1: localStorageFilter?.category1,
          selectedCategory2: localStorageFilter?.category2,
          selectedCategory3: localStorageFilter?.category3,
          selectedCategory4: localStorageFilter?.category4,
          selectedMonth: localStorageFilter?.month,
          selectedProductType: localStorageFilter?.productType,
        };
        await dispatch(filterSortData(objFilter));
      }
    };
    if (!isEmpty(userId)) callFn();
  }, [dispatch, userId, individualDetail]);

  const getUpdatedProducts = useCallback(async (mainData: PlaceholderRow[], modifiedData: PlaceholderRow[]): Promise<UpdatedProduct[]> => {
    const modifiedArray: UpdatedProduct[] = [];
    if (checkArray(modifiedData)) {
      for (const [i, ele] of modifiedData.entries()) {
        const cloneData = mainData[i];
        const dynamicDataCheck = isEqual(ele.dynamicColumn, cloneData?.dynamicColumn);
        const multiDynamicDataCheck = isEqual(ele.multiCustomAttribute, cloneData?.multiCustomAttribute);
        const isChangeCat3 = isEqual(ele.catThreeId, cloneData?.catThreeId);
        const isChangeCat4 = isEqual(ele.catFourId, cloneData?.catFourId);
        const isChangedbrandId = isEqual(ele.brandId, cloneData?.brandId);
        const isChangedMonth = isEqual(ele.month, cloneData?.month);
        const isChangedDescription = isEqual(ele.description, cloneData?.description);
        if (
          !dynamicDataCheck ||
          !multiDynamicDataCheck ||
          !isChangedbrandId ||
          !isChangedMonth ||
          !isChangedDescription ||
          !isChangeCat3 ||
          !isChangeCat4
        ) {
          const objProduct = {
            _id: ele._id,
            description: ele.description,
            catThreeId: ele.catThreeId,
            catFourId: ele.catFourId,
            dynamicColumn: ele.dynamicColumn,
            multiCustomAttribute: ele.multiCustomAttribute,
            brandId: ele.brandId,
            month: ele.month,
          };
          modifiedArray.push(objProduct);
        }
      }
    }
    return modifiedArray;
  }, []);

  const validateFields: { placeholderRequiredKeys: (keyof PlaceholderRow)[] } = {
    placeholderRequiredKeys: ['catOneId', 'catTwoId', 'catThreeId', 'seasonId', 'productTypeId', 'productDesc', 'brandId', 'catThreeId', 'year'],
  };

  const objKeys = useMemo<string[]>(() => ['catOneId', 'catTwoId', 'catThreeId', 'productTypeId', 'brandId', 'subBrandId', 'seasonId', 'year', 'productDesc'], []);

  /**
   * Validates required fields on the placeholder rows and populates the global error map.
   *
   * Which rows are validated is decided by three branches: an explicit selection, new
   * rows lacking a default, or the default draft row. Errors are keyed by the ORIGINAL
   * row index (or _id for the selected branch) so the grid's `errorFor` lookup matches
   * regardless of the row's position.
   *
   * @param selectedIdsArray Optional ids limiting validation to the selected draft rows.
   * @returns True when at least one row has a validation error.
   */
  const checkValidation = useCallback((selectedIdsArray?: string[]): boolean => {
    let hasErrors = false;
    const fieldError: PlaceholderGlobalError = {};
    const rows = placeholderStateRef.current?.placeholderManagement ?? [];
    const hasNewWithoutDefault = rows.some((vl) => vl.isAddRow && !vl._id && !vl.isDefault);

    rows.forEach((element, index) => {
      let key: string | number | null = null;
      if (selectedIdsArray?.length) {
        if (selectedIdsArray.includes(element._id as string) && element.placeholderStatus === 'draft') key = element._id as string;
      } else if (hasNewWithoutDefault) {
        if (element.isAddRow && !element._id && !element.isDefault && element.placeholderStatus === 'draft') key = index;
      } else if (element.isAddRow && element.isDefault && element.placeholderStatus === 'draft') {
        key = index;
      }
      if (key === null) return;
      validateFields?.placeholderRequiredKeys?.forEach((fieldKey) => {
        if (!element[fieldKey]) {
          hasErrors = true;
          set(fieldError, `[${key}][${fieldKey}]`, 'This field is required');
        } else set(fieldError, `[${key}][${fieldKey}]`, '');
      });
    });
    setGlobalErrors(fieldError);
    return hasErrors;
  }, []);

  const createObj = useCallback((obj: CreateObjInput): CreatedObj => {
    const yearRef = obj.year as YearLike | undefined;
    return {
      brandId: { _id: obj.brandId?._id, name: obj.brandId?.name },
      subBrandId: { _id: obj.subBrandId?._id, name: obj.subBrandId?.name },
      seasonId: { _id: obj.seasonId?._id, name: obj.seasonId?.name },
      productTypeId: {
        _id: obj.productTypeId?._id,
        name: obj.productTypeId?.name,
      },
      catOneId: { _id: obj.catOneId?._id, name: obj.catOneId?.name },
      catTwoId: { _id: obj.catTwoId?._id, name: obj.catTwoId?.name },
      catThreeId: { _id: obj.catThreeId?._id, name: obj.catThreeId?.name },
      productDesc: obj.productDesc,
      year: {
        _id: (obj.year as string | number) || yearRef?._id,
        name: (obj.year as string | number) || yearRef?.name,
      },
    };
  }, []);

  const checkDuplicate = useCallback(
    async (array: PlaceholderRow[]): Promise<{ is_Valid: boolean; unique: number[] }> => {
      const data = array.map((datas) => createObj(pick(datas, objKeys)));
      const seen = new Map<string, number>();
      const duplicateIndices: number[] = [];

      data.forEach((item, index) => {
        const serializedItem = JSON.stringify(item);

        if (seen.has(serializedItem)) {
          duplicateIndices.push(index, seen.get(serializedItem) as number);
        } else {
          seen.set(serializedItem, index);
        }
      });
      const uniqueIndices = [...new Set(duplicateIndices)];
      const is_Valid = uniqueIndices.length === 0;
      return { is_Valid, unique: uniqueIndices };
    },
    [createObj, objKeys],
  );

  useEffect(() => {
    return () => {
      setPlaceholderState(initialSetting);
      setCardDetails({
        seasonsId: [],
        month: [],
        year: [],
        category1: [],
        category2: [],
        productTypeId: [],
        brandId: [],
        subBrandId: [],
      });
      setGlobalErrors({});
    };
  }, []);

  const stateValue = useMemo<PlaceholderStateContextValue>(
    () => ({
      placeholderState,
      globalError,
      totalCount,
      cardDetails,
      individualDetail,
      placeholderId,
    }),
    [placeholderState, globalError, totalCount, cardDetails, individualDetail, placeholderId],
  );

  const actionsValue = useMemo<PlaceholderActionsContextValue>(
    () => ({
      setPlaceholderState,
      objKeys,
      converDropdownData,
      getUpdatedProducts,
      checkValidation,
      createObj,
      checkDuplicate,
      addNewRow,
    }),
    [setPlaceholderState, objKeys, converDropdownData, getUpdatedProducts, checkValidation, createObj, checkDuplicate, addNewRow],
  );

  return (
    <StateContext.Provider value={stateValue}>
      <ActionsContext.Provider value={actionsValue}>{children}</ActionsContext.Provider>
    </StateContext.Provider>
  );
}

/**
 * Read the merged Placeholder Management context (state + actions).
 * Throws if used outside the PlaceholderManagementStateProvider.
 */
export function usePlaceholderState(): PlaceholderContextValue {
  const stateContext = useContext(StateContext);
  const actionsContext = useContext(ActionsContext);
  if (!stateContext || !actionsContext) {
    throw new Error('useAppState must be used within the PlaceholderManagementStateProvider');
  }
  return { ...stateContext, ...actionsContext };
}
